import { useQuery } from "@apollo/client";
import { Header, InnerSidebar, InnerSidebarItem } from "@aurem/components";
import {
  Avatar,
  cn,
  Popover,
  Spinner,
  TruncatedContent,
  useBoolean,
} from "@aurem/ui";

import { useCompaniesPaginated } from "hooks/useCompaniesPaginated";
import { ChevronsUpDown, Plus } from "lucide-react";
import { useChatwoot } from "providers/ChatwootProvider";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useMemo, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { gql, PermissionEnum } from "types";
import ROUTES, { companyDashboard } from "../../../const/routes";
import {
  useAuthorize,
  useAuthorizeRender,
  useUserData,
} from "../../../graphql/user";
import { useBasePath } from "../../../hooks/useBasePath";
import { useFeature } from "../../../providers/FeaturesProvider";
import { InternalDataSidebar } from "./InternalDataSidebar";

const CompanySidebarQuery = gql(/* GraphQL */ `
  query CompanySidebar($id: String!) {
    company: findCompany(id: $id) {
      id
      name
    }
  }
`);

const SCROLL_THRESHOLD_PX = 50;

export const LayoutInnerSidebar = () => {
  const { companyId } = useParams();
  const { pathname } = useLocation();

  const shouldShowCompanySidebar =
    companyId && pathname.includes(`/companies/${companyId}`);
  if (shouldShowCompanySidebar) return <LayoutInnerSidebarView />;

  const shouldShowInternalDataSidebar = pathname.includes("/internal/data");
  if (shouldShowInternalDataSidebar) return <InternalDataSidebar />;

  return null;
};

const LayoutInnerSidebarView = () => {
  const { t } = useI18n();
  const { companyId = "" } = useParams();
  const { path } = useBasePath();

  const { data } = useQuery(CompanySidebarQuery, {
    variables: { id: companyId },
    skip: !companyId,
  });

  const {
    companyOptions,
    loading: companiesLoading,
    loadingMore: companiesLoadingMore,
    hasMore,
    onLoadMore,
  } = useCompaniesPaginated();

  const companyName = data?.company?.name;

  const authorizeRender = useAuthorizeRender();
  const canCreateCompany = useAuthorize(PermissionEnum.CreateCompany);
  const isTasksEnabled = useFeature("tasks_feature");
  const chatwoot = useChatwoot();
  const { user } = useUserData();
  // The platform admin's outer sidebar already exposes a Support icon; skip
  // rendering another one in the company-scoped inner sidebar for those users.
  const isPlatformAdmin = useMemo(
    () =>
      user?.userRoles?.some((userRole) => userRole.role.isPlatform) ?? false,
    [user],
  );

  const {
    value: open,
    setValue: setOpen,
    setFalse: handleClose,
  } = useBoolean(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasMore || companiesLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrolledToBottom =
      scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX;

    if (scrolledToBottom) {
      onLoadMore();
    }
  }, [hasMore, companiesLoadingMore, onLoadMore]);

  const header = (
    <Header className="focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-white cursor-pointer">
      {companyName ? (
        <TruncatedContent
          as="h1"
          className="font-medium text-start"
          maxLines={2}
        >
          {companyName}
        </TruncatedContent>
      ) : null}
      <ChevronsUpDown size={16} className="shrink-0" />
    </Header>
  );

  return (
    <InnerSidebar
      header={
        <>
          <Popover
            open={open}
            onOpenChange={setOpen}
            content={
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex flex-col gap-0.5 p-2 max-h-[calc(100vh-8rem)] overflow-y-auto"
              >
                {(companyOptions || []).map((company) => {
                  return (
                    <Link
                      key={company.id}
                      data-e2e-id={`company-selector-${company.id}`}
                      to={path(
                        companyDashboard({
                          companyId: company.id,
                        }),
                      )}
                      className={cn(
                        "relative flex select-none items-center gap-2 rounded-md text-sm p-2 outline-none cursor-pointer",
                        "transition-colors hover:bg-neutral-600/5 focus:bg-neutral-600/5 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                        "ps-3 pe-3",
                        company.id === companyId && "bg-neutral-600/10",
                      )}
                      onClick={handleClose}
                    >
                      <Avatar text={company.text} size="md" />
                      <TruncatedContent
                        as="p"
                        className="text-start"
                        maxLines={2}
                      >
                        {company.text}
                      </TruncatedContent>
                    </Link>
                  );
                })}
                {(companiesLoading || companiesLoadingMore) && (
                  <div className="flex justify-center py-2">
                    <Spinner size="sm" color="neutral" />
                  </div>
                )}
                {canCreateCompany && (
                  <Link
                    className={cn(
                      "relative flex select-none items-center gap-2 rounded-md text-sm py-2 px-3 outline-none cursor-pointer",
                      "transition-colors hover:bg-neutral-600/5 focus:bg-neutral-600/5 active:bg-neutral-600/10 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                    )}
                    to={path(ROUTES.COMPANIES_CREATE_V2)}
                    onClick={handleClose}
                  >
                    <div className="rounded-lg bg-black/10 p-3">
                      <Plus size={16} />
                    </div>
                    <p>{t("apps.admin.navBar.dropdownMenu.addCompany")}</p>
                  </Link>
                )}
              </div>
            }
            placement="right-end"
            contentClassName="p-0"
            offset={{
              mainAxis: 8,
              crossAxis: 8,
            }}
          >
            {header}
          </Popover>
        </>
      }
      footer={
        <>
          {authorizeRender(
            PermissionEnum.ViewActivityLog,
            <InnerSidebarItem
              to={path(`companies/${companyId}/activity`)}
              className="activity-tour-step" /* eslint-disable-line tailwindcss/no-custom-classname */
              label={t("apps.admin.navBar.subNavBar.activities")}
            />,
            companyId,
          )}
          <InnerSidebarItem
            to={path(`companies/${companyId}/details`)}
            className="settings-tour-step" /* eslint-disable-line tailwindcss/no-custom-classname */
            label={t("apps.admin.navBar.subNavBar.companySettings")}
            e2eId="company_settings_button"
          />
          {!isPlatformAdmin && chatwoot.enabled && (
            <InnerSidebarItem
              onClick={chatwoot.show}
              label={t("apps.admin.navBar.support")}
              e2eId="support_button"
            />
          )}
        </>
      }
    >
      {authorizeRender(
        PermissionEnum.ViewCompanyDashboard,
        <InnerSidebarItem
          to={path(`companies/${companyId}/dashboard/overview`)}
          label={t("apps.admin.common.dashboard")}
        />,
        companyId,
      )}
      {isTasksEnabled && (
        <InnerSidebarItem
          to={path(`companies/${companyId}/${ROUTES.TASKS}`)}
          label={t("apps.admin.navBar.tasks")}
        />
      )}
      {authorizeRender(
        PermissionEnum.ListEmployee,
        <InnerSidebarItem
          className="employees-tour-step" /* eslint-disable-line tailwindcss/no-custom-classname */
          to={path(`companies/${companyId}/employees`)}
          label={t("apps.admin.common.employees")}
        />,
        companyId,
      )}
      {authorizeRender(
        PermissionEnum.ViewBenefit,
        <InnerSidebarItem
          className="products-tour-step" /* eslint-disable-line tailwindcss/no-custom-classname */
          to={path(`companies/${companyId}/products`)}
          label={t("apps.admin.navBar.products")}
        />,
        companyId,
      )}
      {authorizeRender(
        PermissionEnum.ViewCompanyPayment,
        <InnerSidebarItem
          className="payments-tour-step" /* eslint-disable-line tailwindcss/no-custom-classname */
          to={path(`companies/${companyId}/payments`)}
          label={t("apps.admin.common.payments")}
        />,
        companyId,
      )}
      {authorizeRender(
        PermissionEnum.ViewBenefit,
        <InnerSidebarItem
          className="requests-tour-step" /* eslint-disable-line tailwindcss/no-custom-classname */
          to={path(`companies/${companyId}/requests`)}
          label={t("apps.admin.navBar.requests")}
        />,
        companyId,
      )}
    </InnerSidebar>
  );
};
